import styled from 'styled-components';
import { doodles } from '../../styles/decor';

const SectionTag = `
    display:inline-flex;
    align-items:center;
    gap:8px;
    color:var(--salato-red);
    font-size:12px;
    font-weight:800;
    letter-spacing:.14em;
    text-transform:uppercase;
    margin-bottom:12px;

    &::before{
        content:'';
        width:18px;
        height:3px;
        border-radius:3px;
        background:var(--salato-yellow);
    }
`;

const SectionTitle = `
    margin:0;
    font-size:clamp(28px, 3.4vw, 44px);
    line-height:1.1;
    letter-spacing:-0.025em;
    color:var(--salato-ink);
    font-weight:800;
`;

export const PageArea = styled.div`
    padding-bottom:96px;
    overflow:hidden;

    .sectionTag{ ${SectionTag} }

    /* ---------- Abertura ---------- */
    .aboutHero{
        position:relative;
        isolation:isolate;

        &::before{
            content:'';
            position:absolute;
            inset:0;
            background-image:${doodles('#ec1c24', .07)};
            background-size:220px;
            -webkit-mask-image:linear-gradient(180deg, #000 0%, #000 55%, transparent 100%);
            mask-image:linear-gradient(180deg, #000 0%, #000 55%, transparent 100%);
            z-index:-1;
        }
    }

    .heroInner{
        position:relative;
        z-index:1;
        max-width:1240px;
        margin:0 auto;
        padding:24px 24px 64px;
        display:grid;
        grid-template-columns:minmax(0, 1.05fr) minmax(0, 1fr);
        align-items:center;
        gap:40px;
    }

    .heroText{
        animation:aboutIn .8s var(--ease-out) both;

        h1{
            margin:0;
            font-size:clamp(34px, 4.6vw, 58px);
            line-height:1.05;
            letter-spacing:-0.035em;
            font-weight:800;
            color:var(--salato-ink);

            em{
                font-style:normal;
                color:var(--salato-red);
            }
        }

        p{
            margin:20px 0 0;
            max-width:520px;
            font-size:18px;
            line-height:1.6;
            color:var(--salato-muted);
        }
    }

    .heroStats{
        display:flex;
        flex-wrap:wrap;
        gap:12px;
        margin-top:28px;

        div{
            padding:14px 18px;
            border-radius:18px;
            background:var(--salato-red);
            color:#fff;
            box-shadow:0 12px 26px rgba(236, 28, 36, .25);
        }

        strong{
            display:block;
            font-size:22px;
            line-height:1.1;
            font-weight:800;
        }

        span{
            display:block;
            margin-top:2px;
            font-size:13px;
            color:var(--salato-yellow);
        }
    }

    .heroMedia{
        position:relative;
        display:flex;
        align-items:center;
        justify-content:center;
        min-height:380px;
        animation:aboutIn .9s var(--ease-out) .15s both;

        img{
            position:relative;
            z-index:1;
            width:min(100%, 560px);
            height:auto;
            filter:drop-shadow(0 26px 34px rgba(91, 33, 6, .2));
        }
    }

    .heroBlob{
        position:absolute;
        width:min(80%, 440px);
        aspect-ratio:1 / 1;
        border-radius:52% 48% 44% 56% / 46% 54% 46% 54%;
        background:linear-gradient(145deg, var(--salato-yellow) 0%, #ffb800 100%);
        animation:aboutBlob 14s ease-in-out infinite alternate;
    }

    @keyframes aboutIn{
        from{ opacity:0; transform:translate3d(0, 24px, 0); }
        to{ opacity:1; transform:none; }
    }

    @keyframes aboutBlob{
        0%{ border-radius:52% 48% 44% 56% / 46% 54% 46% 54%; transform:rotate(0deg); }
        100%{ border-radius:42% 58% 56% 44% / 56% 42% 58% 44%; transform:rotate(-10deg); }
    }

    /* ---------- Valores ---------- */
    .values{
        max-width:1180px;
        margin:0 auto;
        padding:0 24px;
        display:grid;
        grid-template-columns:repeat(3, minmax(0, 1fr));
        gap:16px;
    }

    .valueCard{
        padding:24px;
        border-radius:var(--radius-lg);
        background:#fff;
        box-shadow:0 0 0 1px var(--salato-line), var(--shadow-md);
        transition:transform .3s var(--ease-out), box-shadow .3s ease;

        &:hover{
            transform:translateY(-4px);
            box-shadow:0 0 0 1px transparent, var(--shadow-lg);
        }

        strong{
            display:block;
            margin-top:14px;
            font-size:19px;
            font-weight:800;
            color:var(--salato-ink);
        }

        p{
            margin:6px 0 0;
            color:var(--salato-muted);
            font-size:15px;
            line-height:1.6;
        }
    }

    .valueIcon{
        width:46px;
        height:46px;
        border-radius:14px;
        display:flex;
        align-items:center;
        justify-content:center;
        background:var(--salato-yellow);
        box-shadow:0 6px 14px rgba(255, 184, 0, .35);

        img{
            width:24px;
            height:24px;
            object-fit:contain;
            filter:brightness(0) saturate(100%) invert(17%) sepia(92%) saturate(5235%) hue-rotate(350deg) brightness(93%) contrast(97%);
        }
    }

    /* ---------- Linha do tempo ---------- */
    .timelineSection{
        position:relative;
        isolation:isolate;
        max-width:1100px;
        margin:32px auto 0;
        padding:0 24px;

        &::before{
            content:'';
            position:absolute;
            top:-32px;
            bottom:-40px;
            left:50%;
            width:100vw;
            transform:translateX(-50%);
            background-image:${doodles('#ec1c24', .06)};
            background-size:220px;
            -webkit-mask-image:linear-gradient(180deg, #000 0%, #000 85%, transparent 100%);
            mask-image:linear-gradient(180deg, #000 0%, #000 85%, transparent 100%);
            z-index:-1;
        }
    }

    .sectionHeading{
        max-width:640px;
        margin:0 auto 48px;
        text-align:center;

        .sectionTag{ justify-content:center; }

        h1, h2{ ${SectionTitle} }

        p{
            margin:12px 0 0;
            color:var(--salato-muted);
            font-size:17px;
        }
    }

    .timeline{
        position:relative;
        margin:0;
        padding:0;
        list-style:none;

        &::before{
            content:'';
            position:absolute;
            top:0;
            bottom:0;
            left:50%;
            width:3px;
            margin-left:-1.5px;
            border-radius:3px;
            background:linear-gradient(180deg, var(--salato-yellow) 0%, var(--salato-red) 100%);
        }

        li{
            position:relative;
            width:50%;
            padding:0 40px 32px;

            &.left{ margin-right:50%; }
            &.right{ margin-left:50%; }
        }
    }

    .dot{
        position:absolute;
        top:22px;
        width:18px;
        height:18px;
        border-radius:50%;
        background:#fff;
        box-shadow:0 0 0 4px var(--salato-red), 0 0 0 9px rgba(236, 28, 36, .15);
    }

    li.left .dot{ right:-9px; }
    li.right .dot{ left:-9px; }

    .milestone{
        padding:22px 24px;
        border-radius:22px;
        background:#fff;
        box-shadow:0 0 0 1px var(--salato-line), var(--shadow-md);
        transition:transform .3s var(--ease-out), box-shadow .3s ease;

        &:hover{
            transform:translateY(-3px);
            box-shadow:0 0 0 1px transparent, var(--shadow-lg);
        }

        h3{
            margin:10px 0 0;
            font-size:19px;
            line-height:1.25;
            font-weight:800;
            color:var(--salato-ink);
        }

        p{
            margin:8px 0 0;
            color:var(--salato-muted);
            font-size:15px;
            line-height:1.65;
        }
    }

    .milestonePhotos{
        margin-top:16px;

        &.duo{
            display:grid;
            grid-template-columns:1fr 1fr;
            gap:12px;

            .milestonePhoto button{ aspect-ratio:4 / 5; }
        }
    }

    .milestonePhoto{
        margin:0;

        button{
            position:relative;
            display:block;
            width:100%;
            padding:0;
            border:0;
            border-radius:16px;
            overflow:hidden;
            background:var(--salato-red-soft);
            cursor:zoom-in;
            aspect-ratio:4 / 3;
        }

        img{
            display:block;
            width:100%;
            height:100%;
            object-fit:cover;
            object-position:center 30%;
            transition:transform .5s var(--ease-out);
        }

        button:hover img{ transform:scale(1.04); }

        .zoomHint{
            position:absolute;
            right:10px;
            bottom:10px;
            width:32px;
            height:32px;
            display:grid;
            place-items:center;
            border-radius:50%;
            background:rgba(255,255,255,.92);
            color:var(--salato-red);
            font-size:16px;
            font-weight:800;
            box-shadow:var(--shadow-md);
            opacity:0;
            transform:scale(.8);
            transition:opacity .25s ease, transform .25s var(--ease-out);
        }

        button:hover .zoomHint,
        button:focus-visible .zoomHint{ opacity:1; transform:scale(1); }

        figcaption{
            margin-top:8px;
            color:var(--salato-muted);
            font-size:13px;
            font-style:italic;
        }
    }

    .year{
        display:inline-flex;
        padding:5px 12px;
        border-radius:999px;
        background:var(--salato-red);
        color:#fff;
        font-size:13px;
        font-weight:800;
        letter-spacing:.04em;
    }

    .storyLink{
        text-align:center;
        margin-top:16px;

        button{
            height:48px;
            padding:0 22px;
            border:1.5px solid var(--salato-line);
            border-radius:999px;
            background:#fff;
            color:var(--salato-ink);
            font-size:15px;
            font-weight:700;
            cursor:pointer;
            transition:border-color .2s ease, color .2s ease, transform .25s var(--ease-out);

            &:hover{
                border-color:var(--salato-red);
                color:var(--salato-red);
                transform:translateY(-2px);
            }
        }
    }

    /* ---------- Amigo + produção ---------- */
    .splitSection{
        max-width:1140px;
        margin:96px auto 0;
        padding:0 24px;
        display:grid;
        grid-template-columns:minmax(0, .9fr) minmax(0, 1.1fr);
        align-items:center;
        gap:48px;
    }

    .splitMedia{
        border-radius:28px;
        overflow:hidden;
        box-shadow:var(--shadow-lg);
        transform:rotate(-1.5deg);

        img{
            width:100%;
            height:100%;
            max-height:520px;
            object-fit:cover;
            display:block;
        }
    }

    .splitText{
        h2{ ${SectionTitle} }

        p{
            margin:16px 0 0;
            color:var(--salato-muted);
            font-size:17px;
            line-height:1.7;
        }
    }

    /* ---------- Família ---------- */
    .familySection{
        max-width:1140px;
        margin:96px auto 0;
        padding:0 24px;
        display:grid;
        grid-template-columns:1.05fr 1fr;
        gap:56px;
        align-items:center;
    }

    .familyPhoto{
        position:relative;

        &::before{
            content:'';
            position:absolute;
            inset:18px -18px -18px 18px;
            border-radius:28px;
            background:var(--salato-red);
            z-index:-1;
        }

        button{
            display:block;
            width:100%;
            padding:0;
            border:0;
            border-radius:28px;
            overflow:hidden;
            cursor:zoom-in;
            box-shadow:var(--shadow-lg);
        }

        img{
            display:block;
            width:100%;
            height:auto;
            transition:transform .6s var(--ease-out);
        }

        button:hover img{ transform:scale(1.03); }
    }

    .photoLabel{
        position:absolute;
        left:20px;
        bottom:20px;
        padding:8px 14px;
        border-radius:999px;
        background:var(--salato-yellow);
        color:var(--salato-ink);
        font-size:13px;
        font-weight:800;
        box-shadow:var(--shadow-md);
        pointer-events:none;
    }

    /* ---------- Frase ---------- */
    .quote{
        isolation:isolate;

        blockquote{
            margin:0;
            font-family:'bariol-bold-webfont','Inter',sans-serif;
            font-size:clamp(28px, 3.6vw, 44px);
            line-height:1.2;
            color:var(--salato-red);
        }

        cite{
            display:block;
            margin-top:14px;
            font-style:normal;
            font-weight:700;
            color:var(--salato-muted);
        }
    }

    /* ---------- Hoje ---------- */
    .todaySection{
        position:relative;
        isolation:isolate;
        max-width:860px;
        margin:96px auto 0;
        padding:48px 40px;
        border-radius:28px;
        background:#fff;
        box-shadow:0 0 0 1px var(--salato-line), var(--shadow-md);
        text-align:center;
        overflow:hidden;

        &::before{
            content:'';
            position:absolute;
            inset:0;
            background-image:${doodles('#ec1c24', .07)};
            background-size:220px;
            -webkit-mask-image:radial-gradient(ellipse at center, transparent 35%, #000 100%);
            mask-image:radial-gradient(ellipse at center, transparent 35%, #000 100%);
            z-index:-1;
        }

        .sectionTag{ justify-content:center; }

        h2{ ${SectionTitle} }

        p{
            max-width:600px;
            margin:16px auto 0;
            color:var(--salato-muted);
            font-size:18px;
            line-height:1.65;
        }
    }

    .todayPeople{
        display:flex;
        align-items:center;
        justify-content:center;
        flex-wrap:wrap;
        gap:14px;
        margin-top:28px;

        .person{
            display:inline-flex;
            align-items:center;
            gap:10px;
            padding:8px 18px 8px 8px;
            border-radius:999px;
            background:var(--salato-red-soft);
        }

        .initials{
            width:38px;
            height:38px;
            display:grid;
            place-items:center;
            border-radius:50%;
            background:var(--salato-red);
            color:#fff;
            font-weight:800;
            font-size:16px;
        }

        .initials.yellow{
            background:var(--salato-yellow);
            color:var(--salato-ink);
        }

        strong{
            color:var(--salato-ink);
            font-size:15px;
            font-weight:700;
        }

        .plus{
            color:var(--salato-red);
            font-size:24px;
            font-weight:800;
        }
    }

    /* ---------- Chamada final ---------- */
    .aboutCta{
        max-width:1140px;
        margin:96px auto 0;
        padding:40px 44px;
        border-radius:28px;
        background:
            radial-gradient(500px 240px at 100% 0%, rgba(255, 214, 10, .35), transparent 70%),
            ${doodles('#ffd60a', .22)} 0 0 / 200px,
            var(--salato-red);
        color:#fff;
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:24px;
        box-shadow:0 24px 50px rgba(236, 28, 36, .25);

        h2{
            margin:0;
            font-size:clamp(24px, 2.8vw, 34px);
            font-weight:800;
            letter-spacing:-0.02em;
        }

        p{
            margin:8px 0 0;
            color:rgba(255,255,255,.88);
        }
    }

    .ctaActions{
        display:flex;
        flex-wrap:wrap;
        gap:12px;
    }

    .ctaPrimary,
    .ctaGhost{
        height:52px;
        padding:0 22px;
        display:inline-flex;
        align-items:center;
        justify-content:center;
        border-radius:999px;
        font-weight:700;
        text-decoration:none;
        transition:transform .25s var(--ease-out), background-color .2s ease;

        &:hover{ transform:translateY(-2px); }
    }

    .ctaPrimary{
        background:var(--salato-yellow);
        color:var(--salato-red-strong);
    }

    .ctaGhost{
        color:#fff;
        box-shadow:inset 0 0 0 1.5px rgba(255,255,255,.6);

        &:hover{ background:rgba(255,255,255,.12); }
    }

    /* ---------- Modal da história ilustrada ---------- */
    .storyModal{
        position:fixed;
        inset:0;
        z-index:80;
        display:flex;
        justify-content:center;
        padding:24px;
    }

    .storyBackdrop{
        position:absolute;
        inset:0;
        background:rgba(20, 8, 6, .6);
        backdrop-filter:blur(3px);
    }

    .storyPanel{
        position:relative;
        margin:0;
        align-self:center;
        width:auto;
        max-width:min(100%, 960px);
        background:#111;

        img{
            max-height:calc(100vh - 120px);
            width:auto !important;
            max-width:100%;
            margin:0 auto;
            object-fit:contain;
        }

        figcaption{
            padding:12px 18px 14px;
            background:#fff;
            color:var(--salato-ink);
            font-size:14px;
            font-weight:600;
        }
    }

    .storyPanel.full{
        align-self:stretch;
        width:min(100%, 760px);
        background:#fff;

        img{
            max-height:none;
            width:100% !important;
        }
    }

    .storyPanel{
        max-height:100%;
        overflow-y:auto;
        border-radius:22px;
        background:#fff;
        box-shadow:var(--shadow-lg);
        animation:aboutIn .35s var(--ease-out) both;

        img{
            display:block;
            width:100%;
            height:auto;
        }
    }

    .storyClose{
        position:sticky;
        top:12px;
        float:right;
        margin:12px 12px -56px 0;
        width:44px;
        height:44px;
        border:0;
        border-radius:50%;
        background:#fff;
        color:var(--salato-ink);
        font-size:26px;
        line-height:1;
        cursor:pointer;
        box-shadow:var(--shadow-md);
        z-index:2;
    }

    /* ---------- Responsivo ---------- */
    @media (max-width:900px){
        padding-bottom:72px;

        .heroInner{
            grid-template-columns:1fr;
            padding:16px 18px 48px;
            gap:16px;
            text-align:center;
        }

        .heroText .sectionTag{ justify-content:center; }

        .heroText p{ margin-left:auto; margin-right:auto; font-size:16px; }

        .heroStats{ justify-content:center; }

        .heroMedia{ min-height:0; }

        .values{
            grid-template-columns:1fr;
            padding:0 16px;
        }

        .timelineSection{
            margin-top:24px;
            padding:0 16px;
        }

        .timeline{
            &::before{ left:9px; }

            li,
            li.left,
            li.right{
                width:100%;
                margin:0;
                padding:0 0 20px 36px;
            }
        }

        li.left .dot,
        li.right .dot{
            left:0;
            right:auto;
        }

        .splitSection{
            grid-template-columns:1fr;
            gap:28px;
            margin-top:72px;
            padding:0 16px;
        }

        .splitMedia{ transform:none; }

        .todaySection{
            margin:64px 16px 0;
            padding:36px 20px;

            p{ font-size:16px; }
        }

        .todayPeople{
            flex-direction:column;
            gap:6px;
        }

        .familySection{
            grid-template-columns:1fr;
            gap:40px;
            margin-top:72px;
            padding:0 28px 0 16px;
        }

        .aboutCta{
            margin:72px 16px 0;
            padding:28px 22px;
            flex-direction:column;
            align-items:flex-start;
        }

        .ctaActions{ width:100%; }

        .ctaPrimary,
        .ctaGhost{ flex:1 1 100%; }

        .storyModal{ padding:12px; }
    }
`;
